import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
const DEFAULT_STATE = {
  isStuck: false,
  stuckTop: false,
  stuckBottom: false
} as const;
type Direction = 'top' | 'bottom' | 'both';
export type UseStickySentinelOptions = {
  offsetPx?: number;
  onChange?: (state: {
    isStuck: boolean;
    stuckTop: boolean;
    stuckBottom: boolean;
  }) => void;
  direction?: Direction;
};
export function useStickySentinel(t0) {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offsetPx: t2,
    onChange,
    direction: t3
  } = t1;
  const offsetPx = t2 === undefined ? 0 : t2;
  const direction = t3 === undefined ? "top" : t3;
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      isStuck: false,
      stuckTop: false,
      stuckBottom: false
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [state, setState] = useState(t4);
  let t5;
  let t6;
  if ($[3] !== direction || $[4] !== offsetPx || $[5] !== onChange) {
    t5 = () => {
      const node = topSentinelRef.current;
      if (!node) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry.rootBounds || !entry.target.checkVisibility()) {
          return;
        }
        const {
          boundingClientRect,
          isIntersecting,
          rootBounds
        } = entry;
        const topLine = rootBounds.top + offsetPx;
        const bottomLine = rootBounds.bottom;
        let stuckTop = false;
        let stuckBottom = false;
        if (!isIntersecting) {
          if (boundingClientRect.bottom <= topLine) {
            stuckTop = true;
          }
          if (boundingClientRect.top >= bottomLine) {
            stuckBottom = true;
          }
        }
        let isStuck;
        if (direction === "top") {
          isStuck = stuckTop;
        } else {
          if (direction === "bottom") {
            isStuck = stuckBottom;
          } else {
            isStuck = stuckTop || stuckBottom;
          }
        }
        setState({
          isStuck,
          stuckTop,
          stuckBottom
        });
        onChange?.({
          isStuck,
          stuckTop,
          stuckBottom
        });
      }, {
        root: null,
        threshold: 0
      });
      observer.observe(node);
      return () => {
        observer.disconnect();
      };
    };
    t6 = [direction, offsetPx, onChange];
    $[3] = direction;
    $[4] = offsetPx;
    $[5] = onChange;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== onChange) {
    t7 = () => {
      const node_0 = bottomSentinelRef.current;
      if (!node_0) {
        return;
      }
      const observer_0 = new IntersectionObserver(entries_0 => {
        const entry_0 = entries_0[0];
        if (!entry_0.rootBounds || !entry_0.target.checkVisibility()) {
          const state_0 = {
            isStuck: false,
            stuckTop: false,
            stuckBottom: false
          };
          setState(state_0);
          onChange?.(state_0);
          return;
        }
        if (!entry_0.isIntersecting) {
          setState({
            ...DEFAULT_STATE
          });
          onChange?.({
            ...DEFAULT_STATE
          });
        } else {
          setState({
            isStuck: true,
            stuckTop: false,
            stuckBottom: true
          });
          onChange?.({
            isStuck: true,
            stuckTop: false,
            stuckBottom: true
          });
        }
      }, {
        root: null,
        threshold: 0,
        rootMargin: "0px 0px 0px 0px"
      });
      observer_0.observe(node_0);
      return () => {
        observer_0.disconnect();
      };
    };
    $[8] = onChange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== direction || $[11] !== offsetPx || $[12] !== onChange) {
    t8 = [direction, offsetPx, onChange];
    $[10] = direction;
    $[11] = offsetPx;
    $[12] = onChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== state) {
    t9 = {
      topSentinelRef,
      bottomSentinelRef,
      state
    };
    $[14] = state;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}